import { useState } from 'react';
import { ChevronRight, Receipt, RefreshCw, TriangleAlert, Wallet } from 'lucide-react';
import { getApiError, useGetMyFeesQuery, useGetMyInvoiceQuery, type ParentInvoiceRow } from '@/store/api';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { PageLoader, Spinner } from '@/components/ui/Spinner';
import { formatMinor, methodLabel, termLabel } from '@/lib/money';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const stampFmt = new Intl.DateTimeFormat('en-GH', { day: 'numeric', month: 'short', year: 'numeric' });

/*
  Fees, as a parent sees them.

  ── What this screen is for ────────────────────────────────────────────────

  One question: what do I still owe, and has what I paid been recorded? So the
  balance is the first thing on the page at full size, each child is a card
  under it, and every invoice opens onto its receipts — because the moment a
  parent doubts a payment landed is the moment they ring the office.

  ── Why there is no "pay" button ───────────────────────────────────────────

  SafePass does not take money. Fees are paid at the school or by momo to the
  school's own number, and a button here would imply otherwise. The receipts a
  bursar records are the only acknowledgement this app can honestly show.
*/
export function ParentFeesPage() {
  const { data, isLoading, isError, error, isFetching, refetch } = useGetMyFeesQuery();
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);

  if (isLoading) return <PageLoader label="Loading fees…" />;

  const owes = (data?.balanceMinor ?? 0) > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className={styles.pageTitle}>Fees</h1>
          <p className={cn(styles.meta, 'mt-0.5')}>What is owed, and what has been received.</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Refresh"
          icon={<RefreshCw size={14} className={isFetching ? 'animate-spin' : undefined} />}
          onClick={() => void refetch()}
        />
      </div>

      {isError ? (
        <div className={cn(styles.panel, 'overflow-clip')}>
          <EmptyState
            icon={<TriangleAlert size={17} />}
            title="Could not load fees"
            description={getApiError(error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        </div>
      ) : (data?.children.length ?? 0) === 0 ? (
        <div className={cn(styles.panel, 'overflow-clip')}>
          <EmptyState
            icon={<Wallet size={17} />}
            title="No fees yet"
            description="Nothing has been billed to your children. Invoices appear here as soon as the school raises them."
          />
        </div>
      ) : (
        <>
          {/* ── The figure the whole screen exists to show ───────────────── */}
          <div className={cn(styles.panel, 'px-4 py-3.5')}>
            <p className={styles.label}>Total outstanding</p>
            <p
              className={cn(
                'mt-1 text-[26px] font-bold leading-none tracking-tight tabular-nums',
                owes ? 'text-[hsl(var(--record-ink-danger))]' : 'text-[hsl(var(--record-ink-success))]',
              )}
            >
              {data?.currency} {formatMinor(data?.balanceMinor ?? 0)}
            </p>
            <p className={cn(styles.meta, 'mt-1.5')}>
              {data?.currency} {formatMinor(data?.paidMinor ?? 0)} received of {data?.currency}{' '}
              {formatMinor(data?.billedMinor ?? 0)} billed
              {(data?.overdueCount ?? 0) > 0 && (
                <>
                  {' · '}
                  <span className="font-semibold text-[hsl(var(--record-ink-danger))]">
                    {data?.overdueCount} past due
                  </span>
                </>
              )}
            </p>
          </div>

          {/* ── A card per child ────────────────────────────────────────── */}
          {data?.children.map((child) => (
            <div key={child.studentId} className={cn(styles.panel, 'overflow-clip')}>
              <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-[12.5px] font-bold text-foreground">{child.studentName}</p>
                  {child.studentClass && <p className={styles.meta}>{child.studentClass}</p>}
                </div>
                <span
                  className={cn(
                    styles.figure,
                    'flex-shrink-0 text-[13px]',
                    child.balanceMinor > 0
                      ? 'text-[hsl(var(--record-ink-danger))]'
                      : 'text-[hsl(var(--record-ink-success))]',
                  )}
                >
                  {formatMinor(child.balanceMinor)}
                </span>
              </div>

              {child.invoices.length === 0 ? (
                <p className={cn(styles.panelRow, styles.meta)}>Nothing billed yet.</p>
              ) : (
                <div className="border-t border-border">
                  {child.invoices.map((inv) => (
                    <InvoiceRow key={inv._id} invoice={inv} onOpen={() => setOpenInvoice(inv._id)} />
                  ))}
                </div>
              )}
            </div>
          ))}

          <p className={cn(styles.meta, 'px-1 text-center')}>
            Fees are paid at the school office. Payments appear here once the office records them.
          </p>
        </>
      )}

      <InvoiceSheet invoiceId={openInvoice} onClose={() => setOpenInvoice(null)} />
    </div>
  );
}

function InvoiceRow({ invoice, onOpen }: { invoice: ParentInvoiceRow; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 border-b border-border px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] font-semibold text-foreground">
          {termLabel(invoice.term)} <span className="font-normal text-muted-foreground">· {invoice.academicYear}</span>
        </p>
        <p className={cn(styles.meta, 'mt-0.5')}>
          {invoice.invoiceNumber} · due {stampFmt.format(new Date(invoice.dueDate))}
        </p>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <div className="text-right">
          <p className={cn(styles.figure, 'text-[12px]')}>{formatMinor(invoice.balanceMinor)}</p>
          {invoice.paidMinor > 0 && invoice.balanceMinor > 0 && (
            <p className="text-[10px] tabular-nums text-muted-foreground">
              {formatMinor(invoice.paidMinor)} paid
            </p>
          )}
        </div>
        <StatusMark invoice={invoice} />
        <ChevronRight size={14} className="text-muted-foreground/60" />
      </div>
    </button>
  );
}

function StatusMark({ invoice }: { invoice: { status: string; isOverdue: boolean } }) {
  if (invoice.status === 'paid') return <Mark tone="success">Paid</Mark>;
  if (invoice.isOverdue) return <Mark tone="danger">Overdue</Mark>;
  if (invoice.status === 'part_paid') return <Mark tone="warning">Part paid</Mark>;
  return <Mark tone="neutral">Unpaid</Mark>;
}

function InvoiceSheet({ invoiceId, onClose }: { invoiceId: string | null; onClose: () => void }) {
  const { data: invoice, isLoading } = useGetMyInvoiceQuery(invoiceId ?? '', { skip: !invoiceId });

  return (
    <Modal
      open={invoiceId !== null}
      onClose={onClose}
      title={invoice?.invoiceNumber ?? 'Invoice'}
      description={invoice ? `${invoice.studentName} · ${termLabel(invoice.term)} ${invoice.academicYear}` : undefined}
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {isLoading || !invoice ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          <div className="flex items-center justify-between">
            <div>
              <p className={styles.label}>Balance</p>
              <p
                className={cn(
                  styles.figure,
                  'mt-0.5 text-[20px]',
                  invoice.balanceMinor > 0
                    ? 'text-[hsl(var(--record-ink-danger))]'
                    : 'text-[hsl(var(--record-ink-success))]',
                )}
              >
                {invoice.currency} {formatMinor(invoice.balanceMinor)}
              </p>
            </div>
            <StatusMark invoice={invoice} />
          </div>

          <div className={styles.panel}>
            <p className={cn(styles.label, 'px-3 pt-2.5')}>Billed for</p>
            <ul className="px-3 pb-1.5 pt-1.5">
              {invoice.items.map((item, i) => (
                <li key={`${item.label}-${i}`} className="flex items-center justify-between py-[3px] text-[11.5px]">
                  <span className="text-foreground">{item.label}</span>
                  <span className={styles.figure}>{formatMinor(item.amountMinor)}</span>
                </li>
              ))}
            </ul>
            <div className={cn(styles.panelRow, 'flex items-center justify-between py-2')}>
              <span className="text-[11.5px] font-bold text-foreground">Total</span>
              <span className={cn(styles.figure, 'text-[12.5px]')}>{formatMinor(invoice.totalMinor)}</span>
            </div>
          </div>

          <div>
            <p className={cn(styles.label, 'mb-1.5')}>Payments received</p>
            {invoice.payments.length === 0 ? (
              <p className={styles.meta}>
                Nothing recorded against this invoice yet. If you have paid, the office will record it.
              </p>
            ) : (
              <div className={cn(styles.panel, 'divide-y divide-border')}>
                {invoice.payments.map((p) => (
                  <div key={p._id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground">
                        <Receipt size={11} className="flex-shrink-0 text-muted-foreground" />
                        {methodLabel(p.method)}
                        {p.reference && <span className="font-normal text-muted-foreground">· {p.reference}</span>}
                      </p>
                      <p className={styles.meta}>
                        {p.receiptNumber} · {stampFmt.format(new Date(p.receivedAt))}
                      </p>
                    </div>
                    <span className={cn(styles.figure, 'flex-shrink-0 text-[12px]')}>
                      {formatMinor(p.amountMinor)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
